import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { useProjects } from '../hooks/useProjects';
import AdminForm from '../components/AdminForm';

// DnD Kit Imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableItem from '../components/SortableItem'; // We'll create this next

const AdminPage = () => {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const { projects, addProject, deleteProject, updateProject, reorderProjects } = useProjects();

  const ADMIN_UID = process.env.REACT_APP_ADMIN_UID;

  // Sensors for DnD (prevents clicks from triggering drags accidentally)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSave = async (data) => {
    if (editTarget) {
      await updateProject(editTarget.id, data);
      setEditTarget(null);
    } else {
      await addProject(data);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = projects.findIndex((p) => p.id === active.id);
      const newIndex = projects.findIndex((p) => p.id === over.id);
      
      const newOrder = arrayMove(projects, oldIndex, newIndex);
      reorderProjects(newOrder); // Sync to Firebase
    }
  };

  if (loadingAuth) return <div className="app-container">Verifying Access...</div>;

  if (!user || user.uid !== ADMIN_UID) {
    return (
      <div className="app-container" style={{ display: 'grid', placeItems: 'center', minHeight: '80vh' }}>
        <div className="glass-card" style={{ padding: '40px', textAlign: 'center', maxWidth: '400px' }}>
          <h2 style={{ marginBottom: '20px', letterSpacing: '-1px' }}>Admin Portal</h2>
          <button 
            onClick={() => signInWithPopup(auth, googleProvider)} 
            className="shimmer-text"
            style={{ 
              padding: '12px 24px', borderRadius: '12px', background: '#0f172a', 
              color: '#fff', cursor: 'pointer', border: '1px solid #1e293b', fontWeight: 'bold' 
            }}
          >
            Sign in with Google
          </button>
          {user && <p style={{ color: '#ef4444', marginTop: '15px', fontSize: '0.8rem' }}>Unauthorized Account</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
        <div>
          <h1 style={{ fontSize: '1.8rem', margin: 0 }}>System Control</h1>
          <p style={{ color: '#10b981', fontSize: '0.9rem', margin: 0 }}>Logged in as Soham</p>
        </div>
        <button 
          onClick={() => signOut(auth)} 
          style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' }}
        >
          Logout
        </button>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth > 900 ? '1fr 1fr' : '1fr', gap: '40px', alignItems: 'start' }}>
        <div>
          <AdminForm onAdd={handleSave} editData={editTarget} onCancel={() => setEditTarget(null)} />
        </div>

        <div className="glass-card" style={{ padding: '25px', border: '1px solid #1e293b' }}>
          <h3 style={{ marginBottom: '20px', fontSize: '1rem', color: '#94a3b8' }}>Live Portfolio (Drag to Reorder)</h3>
          
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={projects.map(p => p.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {projects.map(p => (
                  <SortableItem 
                    key={p.id} 
                    project={p} 
                    onEdit={() => { setEditTarget(p); window.scrollTo({top:0, behavior:'smooth'}); }}
                    onDelete={() => { if(window.confirm('Delete this project?')) deleteProject(p.id) }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;